import { useState, FormEvent, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../../components/common/layout/Header";
import Footer from "../../components/common/layout/Footer";
import { GradientButton } from "../../components/common/ui/Button";
import { registerDomain } from "@/services/dreamscapeApi";
import {
  User, MapPin, Globe, Lock, CheckCircle2, AlertCircle,
  ArrowLeft, Loader2, CreditCard, Mail, Phone
} from "lucide-react";

export default function DomainRegister() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const domain = searchParams.get("domain") || "";
  const price = searchParams.get("price") ? parseFloat(searchParams.get("price")!) : undefined;
  const currency = searchParams.get("currency") || "USD";

  const [step, setStep] = useState<number>(1); // 1: Contact Info, 2: Address, 3: Review & Payment, 4: Success
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<{ isPaymentError?: boolean; paymentDetails?: { totalPrice?: string; balance?: string } } | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<number | null>(null);
  // const [registrantId, setRegistrantId] = useState<number | null>(null); // Removed unused state
  const [_accountCreated, setAccountCreated] = useState<boolean>(false);
  // const [paymentCompleted, setPaymentCompleted] = useState<boolean>(false); // Removed unused state

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    organization: "",
    address: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    country: "US",
    period: 1,
    privacy: true,
    nameServers: ["", ""],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!domain) {
      navigate("/domains");
    }
  }, [domain, navigate]);

  const validateStep = (stepNumber: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (stepNumber === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
      if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
      if (!formData.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Invalid email format";
      }
      if (!formData.phone.trim()) {
        newErrors.phone = "Phone is required";
      }
    }

    if (stepNumber === 2) {
      if (!formData.address.trim()) newErrors.address = "Address is required";
      if (!formData.city.trim()) newErrors.city = "City is required";
      if (!formData.state.trim()) newErrors.state = "State/Province is required";
      if (!formData.zip.trim()) newErrors.zip = "ZIP/Postal code is required";
      if (!formData.country.trim()) newErrors.country = "Country is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
      setError(null);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
    setError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateStep(step)) {
      return;
    }

    setLoading(true);
    setError(null);
    setErrorDetails(null);

    try {
      const result = await registerDomain(
        domain,
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address + (formData.address2 ? `, ${formData.address2}` : ""),
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          country: formData.country,
        },
        {
          period: formData.period,
          privacy: formData.privacy,
          nameServers: formData.nameServers.filter(ns => ns.trim() !== ""),
          customerId: customerId || undefined, // Use existing customer ID if available
        }
      );

      if (result.success && result.orderId) {
        setOrderId(result.orderId);
        setSuccess(true);
        if (result.customerId) {
          setCustomerId(result.customerId);
        }
      } else {
        // Check if account was created but registration failed
        if (result.customerId) {
          setCustomerId(result.customerId);
          setAccountCreated(true);
        }

        // Show contact form if needed
        if (result.showContactForm) {
          setStep(1); // Go back to contact form
        }

        setError(result.message || "Registration failed. Please try again.");
        if (result.isPaymentError) {
          setErrorDetails({
            isPaymentError: true,
            paymentDetails: result.paymentDetails,
          });
        }
      }
    } catch (err) {
      console.error("Registration error:", err);
      setError(err instanceof Error ? err.message : "An error occurred during registration.");
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const addNameServer = () => {
    setFormData((prev) => ({
      ...prev,
      nameServers: [...prev.nameServers, ""],
    }));
  };

  const removeNameServer = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      nameServers: prev.nameServers.filter((_, i) => i !== index),
    }));
  };

  const updateNameServer = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      nameServers: prev.nameServers.map((ns, i) => (i === index ? value : ns)),
    }));
  };

  return (
    <div className="min-h-screen text-white bg-[#000000e0]">
      <Header />

      <div className="container py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate("/domains")}
          className="flex items-center gap-2 text-textColor hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Domain Search</span>
        </button>

        {/* Success Screen */}
        {success ? (
          <div className="max-w-2xl mx-auto text-center py-16">
            <div className="bg-cardBg border border-primary/30 rounded-lg p-12">
              <CheckCircle2 className="w-20 h-20 text-green-400 mx-auto mb-6" />
              <h1 className="text-4xl font-bold text-white mb-4">Registration Successful!</h1>
              <p className="text-lg text-textColor mb-2">
                Your domain <span className="text-primary font-semibold">{domain}</span> has been registered.
              </p>
              {orderId && (
                <p className="text-sm text-textColor/70 mb-8">
                  Order ID: <span className="text-white font-mono">{orderId}</span>
                </p>
              )}
              <div className="flex gap-4 justify-center">
                <GradientButton variant="dashboard" onClick={() => navigate("/domains")} className="px-8">
                  Search More Domains
                </GradientButton>
                <button
                  onClick={() => navigate("/")}
                  className="px-8 py-3 bg-cardBg border border-primary/30 rounded-lg text-white hover:bg-primary/10 transition-colors"
                >
                  Go Home
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-12">
              <h1 className="text-5xl font-bold mb-4">Register Domain</h1>
              <div className="flex items-center gap-4">
                <div className="bg-primary/10 border border-primary/30 rounded-lg px-6 py-3">
                  <p className="text-sm text-textColor">Domain Name</p>
                  <p className="text-2xl font-semibold text-white">{domain}</p>
                </div>
                {price && (
                  <div className="bg-primary/10 border border-primary/30 rounded-lg px-6 py-3">
                    <p className="text-sm text-textColor">Price</p>
                    <p className="text-2xl font-semibold text-white">
                      ${price.toFixed(2)} {currency} / year
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center justify-between mb-12">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center flex-1">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold transition-all ${step >= s
                        ? "bg-primary text-white"
                        : "bg-cardBg border-2 border-primary/30 text-textColor"
                      }`}
                  >
                    {step > s ? <CheckCircle2 className="w-6 h-6" /> : s}
                  </div>
                  {s < 3 && (
                    <div
                      className={`flex-1 h-1 mx-4 transition-all ${step > s ? "bg-primary" : "bg-primary/20"
                        }`}
                    />
                  )}
                </div>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="bg-cardBg border border-primary/30 rounded-lg p-8">
              {/* Step 1: Contact Information */}
              {step === 1 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <User className="w-6 h-6 text-primary" />
                    <h2 className="text-2xl font-semibold text-white">Contact Information</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        First Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => updateFormData("firstName", e.target.value)}
                        className={`w-full px-4 py-3 bg-[#000000e0] border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary ${errors.firstName ? "border-red-500" : "border-primary/30"
                          }`}
                        placeholder="John"
                      />
                      {errors.firstName && (
                        <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Last Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => updateFormData("lastName", e.target.value)}
                        className={`w-full px-4 py-3 bg-[#000000e0] border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary ${errors.lastName ? "border-red-500" : "border-primary/30"
                          }`}
                        placeholder="Doe"
                      />
                      {errors.lastName && (
                        <p className="text-red-400 text-xs mt-1">{errors.lastName}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Organization (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.organization}
                      onChange={(e) => updateFormData("organization", e.target.value)}
                      className="w-full px-4 py-3 bg-[#000000e0] border border-primary/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Company Name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Email <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textColor" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => updateFormData("email", e.target.value)}
                        className={`w-full pl-10 pr-4 py-3 bg-[#000000e0] border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary ${errors.email ? "border-red-500" : "border-primary/30"
                          }`}
                        placeholder="john@example.com"
                      />
                    </div>
                    {errors.email && (
                      <p className="text-red-400 text-xs mt-1">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Phone <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textColor" />
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => updateFormData("phone", e.target.value)}
                        className={`w-full pl-10 pr-4 py-3 bg-[#000000e0] border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary ${errors.phone ? "border-red-500" : "border-primary/30"
                          }`}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                    {errors.phone && (
                      <p className="text-red-400 text-xs mt-1">{errors.phone}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Step 2: Address */}
              {step === 2 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <MapPin className="w-6 h-6 text-primary" />
                    <h2 className="text-2xl font-semibold text-white">Address Information</h2>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Street Address <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => updateFormData("address", e.target.value)}
                      className={`w-full px-4 py-3 bg-[#000000e0] border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary ${errors.address ? "border-red-500" : "border-primary/30"
                        }`}
                      placeholder="123 Main Street"
                    />
                    {errors.address && (
                      <p className="text-red-400 text-xs mt-1">{errors.address}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Address Line 2 (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.address2}
                      onChange={(e) => updateFormData("address2", e.target.value)}
                      className="w-full px-4 py-3 bg-[#000000e0] border border-primary/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Suite 100"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        City <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => updateFormData("city", e.target.value)}
                        className={`w-full px-4 py-3 bg-[#000000e0] border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary ${errors.city ? "border-red-500" : "border-primary/30"
                          }`}
                        placeholder="New York"
                      />
                      {errors.city && (
                        <p className="text-red-400 text-xs mt-1">{errors.city}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        State/Province <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => updateFormData("state", e.target.value)}
                        className={`w-full px-4 py-3 bg-[#000000e0] border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary ${errors.state ? "border-red-500" : "border-primary/30"
                          }`}
                        placeholder="NY"
                      />
                      {errors.state && (
                        <p className="text-red-400 text-xs mt-1">{errors.state}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        ZIP/Postal Code <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.zip}
                        onChange={(e) => updateFormData("zip", e.target.value)}
                        className={`w-full px-4 py-3 bg-[#000000e0] border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary ${errors.zip ? "border-red-500" : "border-primary/30"
                          }`}
                        placeholder="10001"
                      />
                      {errors.zip && (
                        <p className="text-red-400 text-xs mt-1">{errors.zip}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Country <span className="text-red-400">*</span>
                      </label>
                      <select
                        value={formData.country}
                        onChange={(e) => updateFormData("country", e.target.value)}
                        className={`w-full px-4 py-3 bg-[#000000e0] border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary ${errors.country ? "border-red-500" : "border-primary/30"
                          }`}
                      >
                        <option value="US">United States</option>
                        <option value="CA">Canada</option>
                        <option value="GB">United Kingdom</option>
                        <option value="AU">Australia</option>
                        <option value="DE">Germany</option>
                        <option value="FR">France</option>
                        <option value="IN">India</option>
                        <option value="JP">Japan</option>
                        <option value="CN">China</option>
                      </select>
                      {errors.country && (
                        <p className="text-red-400 text-xs mt-1">{errors.country}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Domain Settings */}
              {step === 3 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <Globe className="w-6 h-6 text-primary" />
                    <h2 className="text-2xl font-semibold text-white">Domain Settings & Review</h2>
                  </div>

                  {/* Registration Period */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Registration Period
                    </label>
                    <select
                      value={formData.period}
                      onChange={(e) => updateFormData("period", parseInt(e.target.value))}
                      className="w-full px-4 py-3 bg-[#000000e0] border border-primary/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((year) => (
                        <option key={year} value={year}>
                          {year} {year === 1 ? "Year" : "Years"}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* WHOIS Privacy */}
                  <div className="flex items-start gap-3 p-4 bg-[#000000e0] border border-primary/30 rounded-lg">
                    <input
                      type="checkbox"
                      id="privacy"
                      checked={formData.privacy}
                      onChange={(e) => updateFormData("privacy", e.target.checked)}
                      className="mt-1 w-5 h-5 text-primary bg-[#000000e0] border-primary/30 rounded focus:ring-primary"
                    />
                    <div className="flex-1">
                      <label htmlFor="privacy" className="flex items-center gap-2 text-white font-medium cursor-pointer">
                        <Lock className="w-4 h-4" />
                        Enable WHOIS Privacy Protection
                      </label>
                      <p className="text-sm text-textColor mt-1">
                        Hide your personal information from public WHOIS databases
                      </p>
                    </div>
                  </div>

                  {/* Name Servers */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Name Servers (Optional)
                    </label>
                    <p className="text-xs text-textColor mb-3">
                      Leave empty to use default name servers, or specify custom ones
                    </p>
                    <div className="space-y-2">
                      {formData.nameServers.map((ns, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={ns}
                            onChange={(e) => updateNameServer(index, e.target.value)}
                            className="flex-1 px-4 py-2 bg-[#000000e0] border border-primary/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder={`ns${index + 1}.example.com`}
                          />
                          {formData.nameServers.length > 2 && (
                            <button
                              type="button"
                              onClick={() => removeNameServer(index)}
                              className="px-3 py-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                            >
                              <AlertCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addNameServer}
                        className="text-sm text-primary hover:text-primary/80 transition-colors"
                      >
                        + Add Name Server
                      </button>
                    </div>
                  </div>

                  {/* Review Summary */}
                  <div className="bg-[#000000e0] border border-primary/30 rounded-lg p-6 space-y-3">
                    <h3 className="font-semibold text-white mb-4">Review Summary</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between text-textColor">
                        <span>Contact:</span>
                        <span className="text-white">
                          {formData.firstName} {formData.lastName}
                        </span>
                      </div>
                      <div className="flex justify-between text-textColor">
                        <span>Email:</span>
                        <span className="text-white">{formData.email}</span>
                      </div>
                      <div className="flex justify-between text-textColor">
                        <span>Location:</span>
                        <span className="text-white">
                          {formData.city}, {formData.state} {formData.zip}
                        </span>
                      </div>
                      <div className="flex justify-between text-textColor">
                        <span>Period:</span>
                        <span className="text-white">{formData.period} year(s)</span>
                      </div>
                      <div className="flex justify-between text-textColor">
                        <span>Privacy Protection:</span>
                        <span className="text-white">
                          {formData.privacy ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    </div>

                    {/* Price Breakdown */}
                    {price && (
                      <div className="mt-4 pt-4 border-t border-primary/20">
                        <h4 className="font-semibold text-white mb-3">Price Breakdown</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between text-textColor">
                            <span>Domain Registration ({formData.period} {formData.period === 1 ? 'year' : 'years'}):</span>
                            <span className="text-white">
                              ${(price * formData.period).toFixed(2)} {currency}
                            </span>
                          </div>
                          <div className="flex justify-between text-xs text-textColor/70">
                            <span>${price.toFixed(2)} {currency} × {formData.period} {formData.period === 1 ? 'year' : 'years'}</span>
                            <span>= ${(price * formData.period).toFixed(2)} {currency}</span>
                          </div>
                          <div className="flex justify-between items-center mt-3 pt-3 border-t border-primary/10">
                            <span className="font-semibold text-white">Total Price:</span>
                            <span className="text-xl font-bold text-primary">
                              ${(price * formData.period).toFixed(2)} {currency}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className={`mt-6 flex items-start gap-3 rounded-lg p-4 ${errorDetails?.isPaymentError
                    ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
                    : 'bg-red-500/10 border-red-500/30 text-red-400'
                  } border`}>
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">
                      {errorDetails?.isPaymentError ? 'Insufficient Funds' : 'Registration Failed'}
                    </p>
                    <p className="text-sm mb-2">{error}</p>

                    {/* Payment Error Details */}
                    {errorDetails?.isPaymentError && errorDetails.paymentDetails && (
                      <div className="mt-3 p-3 bg-[#000000e0] border border-primary/30 rounded text-xs space-y-2">
                        <div className="space-y-2">
                          {price && (
                            <div className="pb-2 border-b border-primary/20">
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-textColor">Expected Total:</span>
                                <span className="text-white font-semibold">
                                  ${(price * formData.period).toFixed(2)} {currency}
                                </span>
                              </div>
                              <div className="text-textColor/70 text-xs">
                                ${price.toFixed(2)} {currency}/year × {formData.period} {formData.period === 1 ? 'year' : 'years'}
                              </div>
                            </div>
                          )}
                          <div className="flex justify-between items-center">
                            <span className="text-textColor">API Total Price:</span>
                            <span className="text-white font-semibold">${errorDetails.paymentDetails.totalPrice || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-textColor">Current Balance:</span>
                            <span className="text-white font-semibold">${errorDetails.paymentDetails.balance || 'N/A'}</span>
                          </div>
                        </div>
                        <div className="pt-2 mt-2 border-t border-primary/20">
                          <p className="text-textColor mb-1 font-medium">To resolve this issue:</p>
                          <ol className="list-decimal list-inside space-y-1 text-textColor/80">
                            <li>Add funds to your Dreamscape Reseller account</li>
                            <li>Ensure your balance is sufficient to cover the domain registration cost</li>
                            <li>Contact your account manager if you need assistance</li>
                          </ol>
                        </div>
                      </div>
                    )}

                    {/* Customer Error Note */}
                    {error.includes("Customer") && !error.includes("Customer ID is required") && !errorDetails?.isPaymentError && (
                      <div className="mt-3 p-3 bg-[#000000e0] border border-primary/30 rounded text-xs">
                        <p className="text-textColor mb-1">Note: A customer account is automatically created from your information.</p>
                        <p className="text-textColor/80">If this error persists, please check your contact information and try again.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Footer Buttons */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-primary/20">
                <div>
                  {step > 1 && (
                    <button
                      type="button"
                      onClick={handleBack}
                      className="px-6 py-2 text-textColor hover:text-white transition-colors"
                    >
                      Back
                    </button>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => navigate("/domains")}
                    className="px-6 py-2 text-textColor hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  {step < 3 ? (
                    <GradientButton variant="dashboard" type="button" onClick={handleNext} className="px-8">
                      Next
                    </GradientButton>
                  ) : (
                    <GradientButton variant="dashboard" type="submit" disabled={loading} className="px-8 whitespace-nowrap">
                      {loading ? (
                        <span className="flex items-center">
                          <Loader2 className="w-4 h-4 mr-2 animate-spin flex-shrink-0" />
                          <span>Registering...</span>
                        </span>
                      ) : (
                        <span className="flex items-center">
                          <CreditCard className="w-4 h-4 mr-2 flex-shrink-0" />
                          <span>Complete Registration</span>
                        </span>
                      )}
                    </GradientButton>
                  )}
                </div>
              </div>
            </form>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

